import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CalendarCheck, ShieldCheck, Sparkles, ListChecks, Clock, Video } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import api from "@/lib/api";
import { ShareServiceButton } from "@/components/ShareServiceButton";

const HERO_FALLBACK =
    "https://images.unsplash.com/photo-1558478551-1a378f63328e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBicmlnaHQlMjBvZmZpY2UlMjBkZXNrfGVufDB8fHx8MTc4NzE2MDMzOHww&ixlib=rb-4.1.0&q=85&w=1600";

const Eyebrow = ({ children, light }) => (
    <div className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold ${light ? "text-blue-200" : "text-[hsl(var(--accent))]"}`}>
        <span className={`h-[2px] w-6 ${light ? "bg-blue-300" : "bg-[hsl(var(--accent))]"}`} />
        {children}
    </div>
);

const SectionHead = ({ eyebrow, title, link, linkLabel }) => (
    <div className="flex items-end justify-between mb-12 gap-4 flex-wrap">
        <div>
            <Eyebrow>{eyebrow}</Eyebrow>
            <h2 className="font-display text-3xl sm:text-4xl mt-3 max-w-xl">{title}</h2>
        </div>
        {link && (
            <Link to={link} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--primary))] hover:gap-2.5 transition-[gap]">
                {linkLabel} <ArrowRight size={15} />
            </Link>
        )}
    </div>
);

const Hero = ({ settings }) => {
    const heroImg = settings?.hero_image_url || HERO_FALLBACK;
    const trust = [
        [CalendarCheck, "Real-time availability"],
        [ShieldCheck, "Confidential & professional"],
        [Sparkles, "Free discovery call"],
    ];
    return (
        <section className="relative overflow-hidden grid-lines" data-testid="hero-section">
            <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-blue-100/60 blur-3xl" />
            <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-24 lg:pt-24 grid lg:grid-cols-12 gap-12 items-center">
                <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-7">
                    <Eyebrow>{settings?.business_name || "Joseph Kabir Financial Services"}</Eyebrow>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.04] mt-6">
                        Clear-eyed financial <span className="text-[hsl(var(--accent))]">guidance,</span>
                        <br />booked in minutes.
                    </h1>
                    <p className="mt-6 text-base sm:text-lg text-[hsl(var(--muted-foreground))] max-w-xl leading-relaxed">
                        {settings?.tagline ||
                            "Personalized financial guidance for a confident future. Schedule a call, review your plan, or book an in-depth consultation."}
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <Link to="/book" data-testid="hero-book-btn" className="pill btn-primary px-7 py-4 text-sm font-semibold inline-flex items-center gap-2">
                            Book an appointment <ArrowRight size={16} />
                        </Link>
                        <Link to="/services" data-testid="hero-services-btn" className="pill px-7 py-4 text-sm font-semibold border-2 border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))] hover:text-white transition-colors">
                            See services
                        </Link>
                    </div>
                    <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm font-semibold">
                        {trust.map(([Icon, label]) => (
                            <div key={label} className="flex items-center gap-2 text-[hsl(var(--primary))]">
                                <span className="h-7 w-7 rounded-full bg-[hsl(var(--secondary))] grid place-items-center"><Icon size={14} className="text-[hsl(var(--accent))]" /></span>
                                {label}
                            </div>
                        ))}
                    </div>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }} className="lg:col-span-5 relative">
                    <div className="absolute -inset-4 rounded-[32px] bg-[#0A1F5C] rotate-3" />
                    <img src={heroImg} alt="Office" className="relative rounded-[28px] w-full h-[460px] object-cover shadow-2xl" data-testid="hero-image" />
                    <div className="absolute -bottom-8 -left-6 sm:-left-10 bg-white rounded-2xl shadow-xl p-4 pr-6 flex items-center gap-3 border border-[hsl(var(--border))]" data-testid="hero-floating-card">
                        <span className="h-11 w-11 rounded-xl bg-[hsl(var(--accent))] text-white grid place-items-center"><CalendarCheck size={20} /></span>
                        <div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))] font-semibold">Next step</div>
                            <div className="font-display text-sm">Pick a time that suits you</div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

const ServiceCard = ({ s, i }) => (
    <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: i * 0.08 }}
        className="card-clay p-7 flex flex-col group hover:-translate-y-1"
        data-testid={`service-card-${s.id}`}
    >
        <div className="flex items-center justify-between">
            <span className={`text-[11px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full ${s.is_paid ? "bg-[hsl(var(--primary))] text-white" : "bg-blue-50 text-[hsl(var(--accent))]"}`}>
                {s.is_paid ? `$${s.price} ${s.currency}` : "Free"}
            </span>
            <span className="text-xs font-semibold text-[hsl(var(--muted-foreground))] inline-flex items-center gap-1"><Clock size={13} /> {s.duration_min} min</span>
        </div>
        <ShareServiceButton service={s} className="self-start mt-4" />
        <h3 className="font-display text-xl mt-5">{s.title}</h3>
        <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed flex-1">{s.description}</p>
        <Link to={`/book?service=${s.id}`} className="mt-6 inline-flex items-center justify-between text-sm font-semibold text-[hsl(var(--primary))] pt-4 border-t border-[hsl(var(--border))]" data-testid={`home-book-${s.id}`}>
            Book this service
            <span className="h-8 w-8 rounded-full bg-[hsl(var(--secondary))] grid place-items-center group-hover:bg-[hsl(var(--accent))] group-hover:text-white transition-colors"><ArrowUpRight size={15} /></span>
        </Link>
    </motion.div>
);

const Process = () => {
    const steps = [
        [ListChecks, "Choose a service", "Pick the consultation that fits your needs."],
        [CalendarCheck, "Select a time", "See open slots and reserve the one that works."],
        [Video, "Meet with us", "Join in person or online with your confirmation link."],
    ];
    return (
        <section className="navy-panel" data-testid="process-section">
            <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-12 gap-12 items-start">
                <div className="lg:col-span-4">
                    <Eyebrow light>How it works</Eyebrow>
                    <h2 className="font-display text-3xl sm:text-4xl mt-3">Three simple steps to your appointment.</h2>
                </div>
                <div className="lg:col-span-8 grid sm:grid-cols-3 gap-6">
                    {steps.map(([Icon, t, d], i) => (
                        <div key={t} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6 hover:bg-white/10 transition-colors" data-testid={`process-step-${i + 1}`}>
                            <div className="flex items-center justify-between">
                                <Icon size={22} className="text-blue-300" />
                                <span className="font-display text-3xl !text-white/15">0{i + 1}</span>
                            </div>
                            <div className="font-display text-lg mt-6">{t}</div>
                            <p className="text-sm text-white/65 mt-2 leading-relaxed">{d}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

const Posts = ({ posts }) => (
    <section className="max-w-7xl mx-auto px-6 py-24" data-testid="announcements-preview">
        <SectionHead eyebrow="Latest" title="Announcements & posts." link="/announcements" linkLabel="All announcements" />
        <div className="grid md:grid-cols-3 gap-6">
            {posts.map((p) => (
                <Link to={`/post/${p.id}`} key={p.id} className="card-clay overflow-hidden hover:-translate-y-1 group" data-testid={`post-card-${p.id}`}>
                    {p.image_url && <div className="overflow-hidden"><img src={p.image_url} alt={p.title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" /></div>}
                    <div className="p-6">
                        <div className="text-xs font-bold uppercase tracking-widest text-[hsl(var(--accent))]">{new Date(p.created_at).toLocaleDateString()}</div>
                        <div className="font-display text-lg mt-2">{p.title}</div>
                    </div>
                </Link>
            ))}
        </div>
    </section>
);

const Staff = ({ staff }) => (
    <section className="bg-[hsl(var(--secondary))]" data-testid="staff-preview">
        <div className="max-w-7xl mx-auto px-6 py-24">
            <SectionHead eyebrow="The team" title="Meet the people behind your plan." />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {staff.map((m) => (
                    <div key={m.id} className="card-clay p-7 flex gap-5 items-start" data-testid={`staff-card-${m.id}`}>
                        {m.photo_url ? (
                            <img src={m.photo_url} alt={m.name} className="w-16 h-16 rounded-2xl object-cover" />
                        ) : (
                            <div className="w-16 h-16 rounded-2xl bg-[hsl(var(--primary))] text-white grid place-items-center font-display text-xl">{m.name[0]}</div>
                        )}
                        <div>
                            <div className="font-display text-lg">{m.name}</div>
                            <div className="text-xs font-bold uppercase tracking-wider text-[hsl(var(--accent))] mt-0.5">{m.role}</div>
                            <p className="text-sm mt-2 text-[hsl(var(--muted-foreground))] leading-relaxed">{m.bio}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

const CtaBand = () => (
    <section className="max-w-7xl mx-auto px-6 pt-24" data-testid="cta-band">
        <div className="rounded-[28px] bg-[hsl(var(--accent))] text-white p-10 sm:p-14 flex flex-col md:flex-row md:items-center justify-between gap-8 relative overflow-hidden">
            <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-white/10" />
            <div className="relative">
                <h2 className="font-display text-3xl sm:text-4xl !text-white">Ready to talk about your finances?</h2>
                <p className="mt-3 text-white/80 max-w-lg">Choose a service and reserve a time that works for you.</p>
            </div>
            <Link to="/book" data-testid="cta-book-btn" className="relative pill bg-white text-[#0A1F5C] px-7 py-4 text-sm font-bold inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform shrink-0">
                Book an appointment <ArrowRight size={16} />
            </Link>
        </div>
    </section>
);

export default function Home() {
    const [settings, setSettings] = useState(null);
    const [services, setServices] = useState([]);
    const [posts, setPosts] = useState([]);
    const [staff, setStaff] = useState([]);

    useEffect(() => {
        Promise.all([
            api.get("/settings"),
            api.get("/services"),
            api.get("/posts", { params: { only_announcements: true } }),
            api.get("/staff"),
        ]).then(([s, sv, p, st]) => {
            setSettings(s.data);
            setServices(sv.data);
            setPosts(p.data.slice(0, 3));
            setStaff(st.data);
        });
    }, []);

    return (
        <PublicLayout settings={settings}>
            <Hero settings={settings} />
            <section className="max-w-7xl mx-auto px-6 py-24" data-testid="services-preview">
                <SectionHead eyebrow="Services" title="Practical services, thoughtfully priced." link="/services" linkLabel="View all" />
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {services.slice(0, 4).map((s, i) => <ServiceCard key={s.id} s={s} i={i} />)}
                </div>
            </section>
            <Process />
            {posts.length > 0 && <Posts posts={posts} />}
            {staff.length > 0 && <Staff staff={staff} />}
            <CtaBand />
        </PublicLayout>
    );
}
